import { motion } from 'framer-motion'
import { useState } from 'react'

interface TeamNode {
  id: string
  title: string
  description: string
  avatar: 'architect' | 'guardian' | 'specialist' | 'strategist' | 'engineer' | 'innovator'
  connections: string[]
  primaryColor: string
  secondaryColor: string
}

export default function Equipo() {
  const [activeNode, setActiveNode] = useState<string | null>(null)
  const [networkAnimation, setNetworkAnimation] = useState(false)

  const teamNodes: TeamNode[] = [
    {
      id: 'innovation-architect',
      title: 'Chief Innovation Architect',
      description: 'Diseñamos el futuro desde los cimientos del presente',
      avatar: 'architect',
      connections: ['ai-specialist', 'cloud-architect', 'data-strategist'],
      primaryColor: '#0ff6fc',
      secondaryColor: '#7c3aed'
    },
    {
      id: 'ai-specialist',
      title: 'AI Research Specialist',
      description: 'Creamos sistemas que piensan y evolucionan contigo',
      avatar: 'specialist',
      connections: ['innovation-architect', 'data-strategist', 'neural-engineer'],
      primaryColor: '#7c3aed',
      secondaryColor: '#ec4899'
    },
    {
      id: 'cybersecurity-guardian',
      title: 'Cybersecurity Guardian',
      description: 'Defendemos tu información en un mundo digital sin fronteras',
      avatar: 'guardian',
      connections: ['cloud-architect', 'system-engineer', 'innovation-architect'],
      primaryColor: '#ef4444',
      secondaryColor: '#f97316'
    },
    {
      id: 'cloud-architect',
      title: 'Cloud Ecosystem Architect',
      description: 'Construimos infraestructuras que trascienden limitaciones',
      avatar: 'architect',
      connections: ['innovation-architect', 'cybersecurity-guardian', 'system-engineer'],
      primaryColor: '#3b82f6',
      secondaryColor: '#0ff6fc'
    },
    {
      id: 'data-strategist',
      title: 'Data Science Strategist',
      description: 'Transformamos datos en decisiones inteligentes',
      avatar: 'strategist',
      connections: ['ai-specialist', 'innovation-architect', 'neural-engineer'],
      primaryColor: '#10b981',
      secondaryColor: '#06d6a0'
    },
    {
      id: 'neural-engineer',
      title: 'Neural Network Engineer',
      description: 'Tejemos redes neuronales que conectan posibilidades infinitas',
      avatar: 'engineer',
      connections: ['ai-specialist', 'data-strategist', 'ui-innovator'],
      primaryColor: '#8b5cf6',
      secondaryColor: '#a855f7'
    },
    {
      id: 'ui-innovator',
      title: 'Experience Innovation Designer',
      description: 'Materializamos ideas en interfaces que inspiran y conectan',
      avatar: 'innovator',
      connections: ['neural-engineer', 'system-engineer', 'innovation-architect'],
      primaryColor: '#f59e0b',
      secondaryColor: '#fbbf24'
    },
    {
      id: 'system-engineer',
      title: 'Quantum System Engineer',
      description: 'Orquestamos sistemas que operan en perfecta sincronía',
      avatar: 'engineer',
      connections: ['cloud-architect', 'cybersecurity-guardian', 'ui-innovator'],
      primaryColor: '#06b6d4',
      secondaryColor: '#0891b2'
    }
  ]

  // Avatares futuristas y tecnológicos
  const TechAvatar = ({ type, primaryColor, secondaryColor, isActive }: { 
    type: string, 
    primaryColor: string, 
    secondaryColor: string, 
    isActive: boolean 
  }) => {
    const getAvatarContent = () => {
      switch (type) {
        case 'architect':
          return (
            <div className="relative w-full h-full flex items-center justify-center">
              {/* Core structure */}
              <div className="relative">
                {/* Central core */}
                <div 
                  className="w-8 h-8 rounded-lg rotate-45 shadow-lg"
                  style={{ backgroundColor: primaryColor, boxShadow: `0 0 20px ${primaryColor}50` }}
                >
                  <div 
                    className="absolute inset-1 rounded border-2 opacity-60"
                    style={{ borderColor: secondaryColor }}
                  ></div>
                </div>
                
                {/* Architectural lines */}
                <div className="absolute -top-2 -left-2 w-12 h-px" style={{ backgroundColor: primaryColor }}></div>
                <div className="absolute -top-2 -left-2 w-px h-12" style={{ backgroundColor: primaryColor }}></div>
                <div className="absolute -bottom-2 -right-2 w-12 h-px" style={{ backgroundColor: secondaryColor }}></div>
                <div className="absolute -bottom-2 -right-2 w-px h-12" style={{ backgroundColor: secondaryColor }}></div>
                
                {/* Blueprint points */}
                <div className="absolute -top-1 -left-1 w-1 h-1 rounded-full" style={{ backgroundColor: primaryColor }}></div>
                <div className="absolute -top-1 -right-1 w-1 h-1 rounded-full" style={{ backgroundColor: secondaryColor }}></div>
                <div className="absolute -bottom-1 -left-1 w-1 h-1 rounded-full" style={{ backgroundColor: secondaryColor }}></div>
                <div className="absolute -bottom-1 -right-1 w-1 h-1 rounded-full" style={{ backgroundColor: primaryColor }}></div>
              </div>
            </div>
          )
        
        case 'guardian':
          return (
            <div className="relative w-full h-full flex items-center justify-center">
              {/* Shield core */}
              <div className="relative">
                <div 
                  className="w-10 h-12 bg-gradient-to-b rounded-t-full rounded-b-lg border-2 shadow-lg"
                  style={{ 
                    backgroundColor: primaryColor + '20',
                    borderColor: primaryColor,
                    boxShadow: `0 0 25px ${primaryColor}60`
                  }}
                >
                  {/* Central protection symbol */}
                  <div 
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-4 h-4 border-2 rounded"
                    style={{ borderColor: secondaryColor }}
                  >
                    <div 
                      className="absolute inset-1 rounded-sm"
                      style={{ backgroundColor: secondaryColor + '40' }}
                    ></div>
                  </div>
                  
                  {/* Security layers */}
                  <div className="absolute inset-x-1 top-1 h-px" style={{ backgroundColor: primaryColor }}></div>
                  <div className="absolute inset-x-1 bottom-1 h-px" style={{ backgroundColor: primaryColor }}></div>
                </div>
                
                {/* Scanning lines */}
                {isActive && (
                  <>
                    <div className="absolute -left-3 top-0 w-16 h-px animate-pulse" style={{ backgroundColor: primaryColor }}></div>
                    <div className="absolute -left-3 bottom-0 w-16 h-px animate-pulse" style={{ backgroundColor: secondaryColor, animationDelay: '0.5s' }}></div>
                  </>
                )}
              </div>
            </div>
          )
        
        case 'specialist':
          return (
            <div className="relative w-full h-full flex items-center justify-center">
              {/* AI Brain structure */}
              <div className="relative">
                {/* Central processing unit */}
                <div 
                  className="w-8 h-8 rounded-full border-2 shadow-lg"
                  style={{ 
                    borderColor: primaryColor,
                    backgroundColor: primaryColor + '20',
                    boxShadow: `0 0 20px ${primaryColor}50`
                  }}
                >
                  {/* Neural pathways */}
                  <div className="absolute inset-1 rounded-full border" style={{ borderColor: secondaryColor + '60' }}>
                    <div className="absolute top-1 left-1 w-1 h-1 rounded-full" style={{ backgroundColor: primaryColor }}></div>
                    <div className="absolute top-1 right-1 w-1 h-1 rounded-full" style={{ backgroundColor: secondaryColor }}></div>
                    <div className="absolute bottom-1 left-1 w-1 h-1 rounded-full" style={{ backgroundColor: secondaryColor }}></div>
                    <div className="absolute bottom-1 right-1 w-1 h-1 rounded-full" style={{ backgroundColor: primaryColor }}></div>
                  </div>
                </div>
                
                {/* Neural connections */}
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 w-1 h-6" style={{ backgroundColor: primaryColor + '60' }}></div>
                <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-1 h-6" style={{ backgroundColor: secondaryColor + '60' }}></div>
                <div className="absolute -left-3 top-1/2 transform -translate-y-1/2 w-6 h-1" style={{ backgroundColor: primaryColor + '60' }}></div>
                <div className="absolute -right-3 top-1/2 transform -translate-y-1/2 w-6 h-1" style={{ backgroundColor: secondaryColor + '60' }}></div>
                
                {/* Synaptic nodes */}
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 w-2 h-2 rounded-full border" style={{ borderColor: primaryColor }}></div>
                <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-2 h-2 rounded-full border" style={{ borderColor: secondaryColor }}></div>
                <div className="absolute -left-3 top-1/2 transform -translate-y-1/2 w-2 h-2 rounded-full border" style={{ borderColor: primaryColor }}></div>
                <div className="absolute -right-3 top-1/2 transform -translate-y-1/2 w-2 h-2 rounded-full border" style={{ borderColor: secondaryColor }}></div>
              </div>
            </div>
          )
        
        case 'strategist':
          return (
            <div className="relative w-full h-full flex items-center justify-center">
              {/* Data visualization core */}
              <div className="relative">
                {/* Central analytics hub */}
                <div 
                  className="w-6 h-6 border-2 transform rotate-45 shadow-lg"
                  style={{ 
                    borderColor: primaryColor,
                    backgroundColor: primaryColor + '20',
                    boxShadow: `0 0 15px ${primaryColor}40`
                  }}
                ></div>
                
                {/* Data streams */}
                <div className="absolute top-0 left-0 w-full h-full">
                  {/* Vertical data streams */}
                  <div className="absolute left-1 top-0 w-px h-full opacity-60" style={{ backgroundColor: primaryColor }}></div>
                  <div className="absolute right-1 top-0 w-px h-full opacity-60" style={{ backgroundColor: secondaryColor }}></div>
                  
                  {/* Horizontal data streams */}
                  <div className="absolute top-1 left-0 w-full h-px opacity-60" style={{ backgroundColor: primaryColor }}></div>
                  <div className="absolute bottom-1 left-0 w-full h-px opacity-60" style={{ backgroundColor: secondaryColor }}></div>
                  
                  {/* Data points */}
                  <div className="absolute top-2 left-2 w-1 h-1 rounded-full" style={{ backgroundColor: primaryColor }}></div>
                  <div className="absolute top-2 right-2 w-1 h-1 rounded-full" style={{ backgroundColor: secondaryColor }}></div>
                  <div className="absolute bottom-2 left-2 w-1 h-1 rounded-full" style={{ backgroundColor: secondaryColor }}></div>
                  <div className="absolute bottom-2 right-2 w-1 h-1 rounded-full" style={{ backgroundColor: primaryColor }}></div>
                </div>
              </div>
            </div>
          )
        
        case 'engineer':
          return (
            <div className="relative w-full h-full flex items-center justify-center">
              {/* Engineering matrix */}
              <div className="relative">
                {/* Central engineering node */}
                <div 
                  className="w-8 h-8 border-2 shadow-lg"
                  style={{ 
                    borderColor: primaryColor,
                    backgroundColor: primaryColor + '15',
                    boxShadow: `0 0 18px ${primaryColor}45`
                  }}
                >
                  {/* Internal engineering grid */}
                  <div className="absolute inset-1 border border-dashed" style={{ borderColor: secondaryColor + '50' }}>
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-px h-full" style={{ backgroundColor: secondaryColor + '60' }}></div>
                    <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-full h-px" style={{ backgroundColor: secondaryColor + '60' }}></div>
                  </div>
                  
                  {/* Engineering anchor points */}
                  <div className="absolute top-0 left-0 w-2 h-2 border transform -translate-x-1/2 -translate-y-1/2" style={{ borderColor: primaryColor }}></div>
                  <div className="absolute top-0 right-0 w-2 h-2 border transform translate-x-1/2 -translate-y-1/2" style={{ borderColor: secondaryColor }}></div>
                  <div className="absolute bottom-0 left-0 w-2 h-2 border transform -translate-x-1/2 translate-y-1/2" style={{ borderColor: secondaryColor }}></div>
                  <div className="absolute bottom-0 right-0 w-2 h-2 border transform translate-x-1/2 translate-y-1/2" style={{ borderColor: primaryColor }}></div>
                </div>
                
                {/* External connection ports */}
                <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-4 h-1" style={{ backgroundColor: primaryColor }}></div>
                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-4 h-1" style={{ backgroundColor: secondaryColor }}></div>
                <div className="absolute -left-2 top-1/2 transform -translate-y-1/2 w-1 h-4" style={{ backgroundColor: primaryColor }}></div>
                <div className="absolute -right-2 top-1/2 transform -translate-y-1/2 w-1 h-4" style={{ backgroundColor: secondaryColor }}></div>
              </div>
            </div>
          )
        
        case 'innovator':
          return (
            <div className="relative w-full h-full flex items-center justify-center">
              {/* Innovation spark */}
              <div className="relative">
                {/* Central creative core */}
                <div 
                  className="w-6 h-6 rounded-full border-2 shadow-lg"
                  style={{ 
                    borderColor: primaryColor,
                    backgroundColor: primaryColor + '25',
                    boxShadow: `0 0 20px ${primaryColor}50`
                  }}
                >
                  {/* Creative energy patterns */}
                  <div className="absolute inset-1 rounded-full border border-dashed" style={{ borderColor: secondaryColor + '70' }}>
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1 h-1 rounded-full" style={{ backgroundColor: primaryColor }}></div>
                  </div>
                </div>
                
                {/* Innovation rays */}
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-full w-px h-4" style={{ backgroundColor: primaryColor + '70' }}></div>
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full w-px h-4" style={{ backgroundColor: secondaryColor + '70' }}></div>
                <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-full w-4 h-px" style={{ backgroundColor: primaryColor + '70' }}></div>
                <div className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-full w-4 h-px" style={{ backgroundColor: secondaryColor + '70' }}></div>
                
                {/* Diagonal innovation sparks */}
                <div className="absolute top-0 left-0 transform -translate-x-2 -translate-y-2 w-2 h-px rotate-45" style={{ backgroundColor: primaryColor }}></div>
                <div className="absolute top-0 right-0 transform translate-x-2 -translate-y-2 w-2 h-px -rotate-45" style={{ backgroundColor: secondaryColor }}></div>
                <div className="absolute bottom-0 left-0 transform -translate-x-2 translate-y-2 w-2 h-px -rotate-45" style={{ backgroundColor: secondaryColor }}></div>
                <div className="absolute bottom-0 right-0 transform translate-x-2 translate-y-2 w-2 h-px rotate-45" style={{ backgroundColor: primaryColor }}></div>
              </div>
            </div>
          )
        
        default:
          return null
      }
    }

    return (
      <div className="relative w-full h-full">
        {/* Neural network background pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="grid grid-cols-6 grid-rows-6 h-full w-full">
            {[...Array(36)].map((_, i) => (
              <div 
                key={i} 
                className={`border-opacity-30 ${i % 7 === 0 ? 'border' : ''}`}
                style={{ borderColor: primaryColor }}
              ></div>
            ))}
          </div>
        </div>
        
        {/* Main avatar content */}
        <div className="relative z-10">
          {getAvatarContent()}
        </div>
        
        {/* Holographic overlay effect */}
        <div 
          className={`absolute inset-0 rounded-full opacity-30 ${isActive ? 'animate-pulse' : ''}`}
          style={{ backgroundColor: primaryColor + '15' }}
        ></div>
      </div>
    )
  }

  return (
    <div className="pt-32 pb-20 bg-gradient-to-br from-slate-950 via-blue-950/20 to-slate-950 min-h-screen relative overflow-hidden">
      {/* Neural Network Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 opacity-10">
          {/* Dynamic neural connections */}
          <svg className="w-full h-full">
            {teamNodes.map((node, index) => {
              const centerX = 50 + (index % 4) * 25
              const centerY = 20 + Math.floor(index / 4) * 30
              
              return node.connections.map((connectionId, connIndex) => {
                const targetIndex = teamNodes.findIndex(n => n.id === connectionId)
                if (targetIndex === -1) return null
                
                const targetX = 50 + (targetIndex % 4) * 25
                const targetY = 20 + Math.floor(targetIndex / 4) * 30
                
                return (
                  <line
                    key={`${node.id}-${connectionId}`}
                    x1={`${centerX}%`}
                    y1={`${centerY}%`}
                    x2={`${targetX}%`}
                    y2={`${targetY}%`}
                    stroke={node.primaryColor}
                    strokeWidth="1"
                    strokeOpacity="0.3"
                    className={activeNode === node.id || activeNode === connectionId ? 'animate-pulse' : ''}
                  />
                )
              })
            })}
          </svg>
        </div>
        
        {/* Floating particles */}
        <div className="absolute inset-0">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-cyan-400/30 rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 2}s`
              }}
            ></div>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.h1 
            className="text-5xl md:text-7xl font-bold mb-8 bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent"
            onMouseEnter={() => setNetworkAnimation(true)}
            onMouseLeave={() => setNetworkAnimation(false)}
          >
            Neural Collective
          </motion.h1>
          
          <div className="w-32 h-1 bg-gradient-to-r from-cyan-400 to-purple-600 mx-auto mb-8"></div>
          
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed mb-12">
            Somos una red inteligente de especialistas interconectados, donde cada nodo representa 
            una expertise única que contribuye a un organismo colectivo más poderoso que la suma de sus partes.
          </p>

          {/* Network Status Indicator */}
          <div className="flex items-center justify-center space-x-4 text-sm text-gray-400">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
              <span>Red Neuronal: ACTIVA</span>
            </div>
            <div className="w-px h-4 bg-gray-600"></div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-cyan-400 rounded-full animate-pulse" style={{animationDelay: '0.5s'}}></div>
              <span>Procesamiento: CONTINUO</span>
            </div>
            <div className="w-px h-4 bg-gray-600"></div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-purple-400 rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
              <span>Innovación: ILIMITADA</span>
            </div>
          </div>
        </motion.div>

        {/* Neural Network Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {teamNodes.map((node, index) => (
            <motion.div
              key={node.id}
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              onMouseEnter={() => setActiveNode(node.id)}
              onMouseLeave={() => setActiveNode(null)}
              className="group relative"
            >
              <div className="relative">
                {/* Node Container */}
                <div 
                  className="relative p-8 bg-gradient-to-br from-slate-900/80 to-slate-950/90 rounded-2xl border backdrop-blur-md transition-all duration-500 overflow-hidden group-hover:scale-105"
                  style={{ 
                    borderColor: activeNode === node.id ? node.primaryColor : '#374151',
                    boxShadow: activeNode === node.id ? `0 0 30px ${node.primaryColor}30` : 'none'
                  }}
                >
                  {/* Neural activity background */}
                  <div 
                    className={`absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-500`}
                    style={{ 
                      background: `radial-gradient(circle at center, ${node.primaryColor}15 0%, transparent 70%)`
                    }}
                  ></div>

                  {/* Connection indicators */}
                  <div className="absolute top-4 right-4 flex space-x-1">
                    {node.connections.map((_, connIndex) => (
                      <div
                        key={connIndex}
                        className="w-2 h-2 rounded-full animate-pulse"
                        style={{ 
                          backgroundColor: connIndex % 2 === 0 ? node.primaryColor : node.secondaryColor,
                          animationDelay: `${connIndex * 0.3}s`
                        }}
                      ></div>
                    ))}
                  </div>

                  {/* Avatar Section */}
                  <div className="relative mb-6">
                    <div className="w-28 h-28 mx-auto relative">
                      {/* Outer energy ring */}
                      <div 
                        className={`absolute inset-0 rounded-full border-2 transition-all duration-500 ${
                          activeNode === node.id ? 'animate-spin' : ''
                        }`}
                        style={{ 
                          borderColor: node.primaryColor,
                          animationDuration: '4s'
                        }}
                      >
                        {/* Orbital indicators */}
                        <div 
                          className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 rounded-full"
                          style={{ backgroundColor: node.primaryColor }}
                        ></div>
                        <div 
                          className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 rounded-full"
                          style={{ backgroundColor: node.secondaryColor }}
                        ></div>
                      </div>
                      
                      {/* Avatar container */}
                      <div className="absolute inset-2 rounded-full bg-slate-900/50 backdrop-blur-sm border" style={{ borderColor: node.secondaryColor + '40' }}>
                        <TechAvatar 
                          type={node.avatar} 
                          primaryColor={node.primaryColor}
                          secondaryColor={node.secondaryColor}
                          isActive={activeNode === node.id}
                        />
                      </div>
                      
                      {/* Energy pulses */}
                      {activeNode === node.id && (
                        <div 
                          className="absolute inset-0 rounded-full animate-ping"
                          style={{ backgroundColor: node.primaryColor + '20' }}
                        ></div>
                      )}
                    </div>
                  </div>

                  {/* Node Information */}
                  <div className="text-center space-y-4">
                    <h3 
                      className="text-lg font-bold transition-colors duration-300"
                      style={{ 
                        color: activeNode === node.id ? node.primaryColor : '#ffffff'
                      }}
                    >
                      {node.title}
                    </h3>
                    
                    <p className="text-gray-300 text-sm leading-relaxed group-hover:text-gray-200 transition-colors duration-300">
                      {node.description}
                    </p>

                    {/* Connection status */}
                    <div className="flex items-center justify-center space-x-2 text-xs text-gray-400">
                      <div 
                        className="w-1 h-1 rounded-full"
                        style={{ backgroundColor: node.primaryColor }}
                      ></div>
                      <span>{node.connections.length} conexiones activas</span>
                    </div>
                  </div>

                  {/* Quantum effects */}
                  <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                    {/* Corner quantum dots */}
                    <div 
                      className="absolute top-2 left-2 w-1 h-1 rounded-full animate-pulse"
                      style={{ backgroundColor: node.primaryColor }}
                    ></div>
                    <div 
                      className="absolute top-2 right-2 w-1 h-1 rounded-full animate-pulse"
                      style={{ backgroundColor: node.secondaryColor, animationDelay: '0.5s' }}
                    ></div>
                    <div 
                      className="absolute bottom-2 left-2 w-1 h-1 rounded-full animate-pulse"
                      style={{ backgroundColor: node.secondaryColor, animationDelay: '1s' }}
                    ></div>
                    <div 
                      className="absolute bottom-2 right-2 w-1 h-1 rounded-full animate-pulse"
                      style={{ backgroundColor: node.primaryColor, animationDelay: '1.5s' }}
                    ></div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Collective Intelligence Statement */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center"
        >
          <div className="p-10 bg-gradient-to-r from-slate-900/50 to-slate-950/50 rounded-3xl border border-slate-700/50 backdrop-blur-md relative overflow-hidden">
            {/* Background tech pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="grid grid-cols-20 grid-rows-10 h-full w-full">
                {[...Array(200)].map((_, i) => (
                  <div 
                    key={i} 
                    className={`border-cyan-400 ${i % 17 === 0 ? 'border border-opacity-30' : ''}`}
                  ></div>
                ))}
              </div>
            </div>
            
            <div className="relative z-10">
              <h3 className="text-3xl font-bold text-gradient mb-6">
                Inteligencia Colectiva en Acción
              </h3>
              <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed mb-8">
                Cada especialista es un nodo en nuestra red neuronal empresarial. 
                Juntos procesamos información, generamos soluciones y evolucionamos 
                continuamente para superar cualquier desafío tecnológico.
              </p>
              
              <div className="flex items-center justify-center space-x-8 text-sm">
                <div className="flex items-center space-x-2 text-cyan-400">
                  <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
                  <span>Procesamiento Distribuido</span>
                </div>
                <div className="flex items-center space-x-2 text-purple-400">
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" style={{animationDelay: '0.3s'}}></div>
                  <span>Aprendizaje Continuo</span>
                </div>
                <div className="flex items-center space-x-2 text-blue-400">
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" style={{animationDelay: '0.6s'}}></div>
                  <span>Evolución Adaptativa</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
